/**
 * SkillsSection — Agent Skills management UI for the Settings page.
 *
 * Three scopes are shown together with visual separators:
 *   - bundled  (read-only, lock icon, can still toggle on/off)
 *   - user     (editable, ~/.nbyula-cowork/skills/)
 *   - project  (editable, <cwd>/.cowork/skills/)
 *
 * Bundled skills cannot be edited or deleted from the GUI. We don't add
 * any filesystem guard — a tech-savvy user can still edit the shipped
 * files directly on disk at their own risk.
 *
 * Components in this file:
 *   - SkillsSection         List view with filters + per-row actions
 *   - AddSkillModal         Install from source OR create blank
 *   - SkillEditorPanel      Full editor for a single skill
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSkillsStore } from "@/stores/skillsStore";
import { useMcpStore } from "@/stores/mcpStore";
import { Button } from "@/components/common/Button";
import type {
  Skill,
  SkillPreviewItem,
  SkillScope,
} from "@/api/types";

type ScopeFilter = "all" | SkillScope;

// ─── top-level section ────────────────────────────────────────────────────

export function SkillsSection() {
  const { skills, isLoading, loadSkills } = useSkillsStore();
  const [filter, setFilter] = useState<ScopeFilter>("all");
  const [search, setSearch] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editing, setEditing] = useState<Skill | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    void loadSkills().catch(() => {});
  }, [loadSkills]);

  // Pick up skills hand-added/removed on disk while the page is open.
  // The backend rescans every GET /skills, so a focus-triggered reload
  // gives near-real-time detection without a watcher.
  useEffect(() => {
    const onFocus = () => {
      void loadSkills().catch(() => {});
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadSkills]);

  const runRefresh = async () => {
    setRefreshing(true);
    try {
      await loadSkills();
    } finally {
      setRefreshing(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return skills.filter((s) => {
      if (filter !== "all" && s.scope !== filter) return false;
      if (!q) return true;
      return (
        s.name.toLowerCase().includes(q) ||
        (s.description ?? "").toLowerCase().includes(q)
      );
    });
  }, [skills, filter, search]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-space">Agent Skills</h2>
          <p className="text-xs text-space-40 mt-0.5">
            Reusable workflows the agent can invoke. Install from GitHub or
            write your own. Follows the{" "}
            <a
              href="https://agentskills.io/specification"
              target="_blank"
              rel="noreferrer"
              className="text-jove hover:underline"
            >
              Agent Skills open spec
            </a>
            .
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 self-start sm:self-center">
          <button
            type="button"
            onClick={runRefresh}
            disabled={refreshing || isLoading}
            title="Rescan skills folders on disk"
            aria-label="Refresh skills"
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-space-30 text-space-50 hover:text-jove hover:border-jove-60/40 hover:bg-jove-10/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg
              className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
          <Button
            size="sm"
            onClick={() => setAddModalOpen(true)}
            className="whitespace-nowrap"
          >
            + Add Skill
          </Button>
        </div>
      </header>

      {/* Filters + search */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
        <div className="flex rounded-lg border border-space-30 overflow-hidden text-xs shrink-0">
          {(["all", "bundled", "installed"] as ScopeFilter[]).map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`flex-1 sm:flex-none px-3 py-1.5 capitalize transition-colors ${
                filter === s
                  ? "bg-primary text-on-primary"
                  : "bg-surface text-space-60 hover:bg-space-10"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-0">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-space-40 pointer-events-none"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
            />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search skills…"
            className="w-full rounded-lg border border-space-30 pl-8 pr-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-jove-60/20 focus:border-jove-60 transition-colors"
          />
        </div>
      </div>

      {/* Skill list */}
      {isLoading && skills.length === 0 ? (
        <p className="text-xs text-space-40 italic py-4 text-center">Loading skills…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-space-30 p-8 text-center">
          <p className="text-sm text-space-40">
            {skills.length === 0
              ? "No skills yet. Install one from a GitHub repo or create a blank skill."
              : "No skills match the current filters."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((skill) => (
            <SkillRow
              key={skill.id}
              skill={skill}
              onEdit={() => setEditing(skill)}
            />
          ))}
        </div>
      )}

      {addModalOpen && (
        <AddSkillModal
          onClose={() => setAddModalOpen(false)}
          onCreated={(fresh) => {
            setAddModalOpen(false);
            if (fresh) setEditing(fresh);
          }}
        />
      )}

      {editing && (
        <SkillEditorModal
          skillId={editing.id}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

// ─── row ──────────────────────────────────────────────────────────────────

function SkillRow({ skill, onEdit }: { skill: Skill; onEdit: () => void }) {
  const { toggleSkill, deleteSkill, updateFromSource } = useSkillsStore();
  const [busy, setBusy] = useState<string | null>(null);
  const isBundled = skill.scope === "bundled";
  const hasErrors = skill.errors.length > 0;

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    try {
      await fn();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className={`rounded-xl border p-4 transition group ${
        hasErrors
          ? "border-red-300 bg-red-50/30"
          : "border-space-30 bg-surface hover:border-jove-60/30 hover:shadow-sm"
      }`}
    >
      {/* Top: info + toggle */}
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-base leading-none" aria-hidden>
              {isBundled ? "🔒" : "✏️"}
            </span>
            <h3 className="font-semibold text-sm text-space">{skill.name}</h3>
            <ScopeBadge scope={skill.scope} />
            {!skill.enabled && (
              <span className="text-[10px] uppercase tracking-wider font-medium text-space-40 bg-space-10 px-1.5 py-0.5 rounded">
                disabled
              </span>
            )}
          </div>
          <p className="text-xs text-space-50 mt-1 line-clamp-2 leading-relaxed">
            {skill.description || <span className="italic text-space-40">No description</span>}
          </p>

          {/* Metadata row */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-space-40">
            {skill.requires_mcp.length > 0 && (
              <span className="inline-flex items-center gap-1">
                <span className="text-space-50">Requires:</span>
                {skill.requires_mcp.map((m) => (
                  <code
                    key={m}
                    className="bg-space-10 text-space-60 rounded px-1.5 py-px"
                  >
                    {m}
                  </code>
                ))}
              </span>
            )}
            {skill.source_url && (
              <span className="truncate max-w-xs sm:max-w-sm text-space-40">
                from{" "}
                <code className="text-space-50">
                  {skill.source_url.replace(/\.git$/, "").replace(/^https?:\/\/github\.com\//, "")}
                </code>
                {skill.commit_sha && (
                  <code className="ml-1 text-space-40">
                    @{skill.commit_sha.slice(0, 7)}
                  </code>
                )}
              </span>
            )}
          </div>

          {hasErrors && (
            <ul className="mt-2 text-[11px] text-red-600 list-disc list-inside space-y-0.5">
              {skill.errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          )}
        </div>

        {/* Toggle switch — always visible, top-right */}
        <ToggleSwitch
          checked={skill.enabled}
          disabled={busy === "toggle"}
          onChange={(v) => run("toggle", () => toggleSkill(skill.id, v))}
          label={skill.enabled ? "On" : "Off"}
        />
      </div>

      {/* Bottom: action buttons — responsive wrap */}
      <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-space-20">
        <Button
          size="sm"
          variant={isBundled ? "ghost" : "secondary"}
          onClick={onEdit}
        >
          {isBundled ? "View" : "Edit"}
        </Button>

        {skill.source_url && !isBundled && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => run("update", () => updateFromSource(skill.id))}
            disabled={busy === "update"}
            loading={busy === "update"}
          >
            Update
          </Button>
        )}

        {/* Spacer pushes delete to the right on wider screens */}
        {!isBundled && <div className="flex-1" />}

        {!isBundled && (
          <Button
            size="sm"
            variant="danger"
            onClick={() => {
              if (confirm(`Delete skill "${skill.name}"?`)) {
                run("delete", () => deleteSkill(skill.id));
              }
            }}
            disabled={busy === "delete"}
            loading={busy === "delete"}
          >
            Delete
          </Button>
        )}
      </div>
    </div>
  );
}

function ScopeBadge({ scope }: { scope: SkillScope }) {
  const styles: Record<SkillScope, string> = {
    bundled: "bg-space-10 text-space-60 border border-space-20",
    installed: "bg-jove-10 text-jove border border-jove-30",
  };
  return (
    <span
      className={`text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded ${styles[scope]}`}
    >
      {scope}
    </span>
  );
}

// ─── add modal ────────────────────────────────────────────────────────────

function AddSkillModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (fresh: Skill | null) => void;
}) {
  const { previewSource, installFromSource, createSkill } = useSkillsStore();
  const [tab, setTab] = useState<"install" | "create">("install");

  // Install-from-source state
  const [source, setSource] = useState("");
  const [preview, setPreview] = useState<SkillPreviewItem[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [installing, setInstalling] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [installError, setInstallError] = useState<string | null>(null);

  // Create-new state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const runPreview = async () => {
    setInstallError(null);
    setPreview(null);
    setSelected(new Set());
    setPreviewing(true);
    try {
      const res = await previewSource(source.trim());
      setPreview(res.skills);
      setSelected(new Set(res.skills.map((s) => s.name)));
    } catch (err) {
      setInstallError((err as Error).message);
    } finally {
      setPreviewing(false);
    }
  };

  const runInstall = async () => {
    if (!preview) return;
    setInstalling(true);
    setInstallError(null);
    try {
      const selectedNames =
        selected.size === preview.length ? undefined : Array.from(selected);
      await installFromSource(source.trim(), selectedNames);
      onCreated(null);
    } catch (err) {
      setInstallError((err as Error).message);
    } finally {
      setInstalling(false);
    }
  };

  const runCreate = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const fresh = await createSkill({
        name: name.trim(),
        description: description.trim(),
        body: "## Overview\n\nDescribe what this skill does.\n",
      });
      onCreated(fresh);
    } catch (err) {
      const msg = (err as Error).message;
      setCreateError(msg);
    } finally {
      setCreating(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-surface rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-2xl max-h-[90vh] sm:max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <header className="px-5 py-4 border-b border-space-20 flex items-center justify-between shrink-0">
          <h2 className="text-base font-semibold text-space">Add Skill</h2>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-space-40 hover:text-space-60 hover:bg-space-10 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        {/* Tabs */}
        <div className="border-b border-space-20 px-5 flex gap-1 shrink-0">
          {(["install", "create"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                tab === t
                  ? "border-jove text-jove"
                  : "border-transparent text-space-40 hover:text-space-60"
              }`}
            >
              {t === "install" ? "Install from source" : "Create new"}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto flex-1 min-h-0">
          {tab === "install" && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-space-60 mb-1.5">
                  Source
                </label>
                <input
                  type="text"
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && source.trim()) runPreview(); }}
                  placeholder="vercel-labs/agent-skills or https://github.com/…"
                  className="w-full rounded-lg border border-space-30 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-jove-60/20 focus:border-jove-60 transition-colors"
                />
                <p className="text-[11px] text-space-40 mt-1.5 leading-relaxed">
                  Supports: <code className="bg-space-10 px-1 rounded">owner/repo</code>,
                  full URL, <code className="bg-space-10 px-1 rounded">/tree/branch/path</code>,
                  GitLab, SSH git, or local path.
                </p>
              </div>

              <Button
                size="sm"
                variant="secondary"
                onClick={runPreview}
                disabled={!source.trim() || previewing}
                loading={previewing}
              >
                Browse skills
              </Button>

              {installError && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                  {installError}
                </div>
              )}

              {preview && preview.length === 0 && (
                <p className="text-xs text-space-40 italic text-center py-4">
                  No SKILL.md files found in this source.
                </p>
              )}

              {preview && preview.length > 0 && (
                <div>
                  {/* Select all / deselect all header */}
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-space-50">
                      {selected.size} of {preview.length} selected
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        if (selected.size === preview.length) {
                          setSelected(new Set());
                        } else {
                          setSelected(new Set(preview.map((s) => s.name)));
                        }
                      }}
                      className="text-xs text-jove hover:text-jove-200 font-medium transition-colors"
                    >
                      {selected.size === preview.length ? "Deselect all" : "Select all"}
                    </button>
                  </div>
                  <div className="border border-space-20 rounded-xl overflow-hidden max-h-64 overflow-y-auto">
                    {preview.map((item, idx) => {
                      const isChecked = selected.has(item.name);
                      return (
                        <label
                          key={item.name}
                          className={`flex items-start gap-3 px-3.5 py-3 cursor-pointer transition-colors ${
                            idx > 0 ? "border-t border-space-20" : ""
                          } ${
                            isChecked
                              ? "bg-jove-10/50 hover:bg-jove-10"
                              : "bg-surface hover:bg-space-10/50"
                          }`}
                        >
                          <div className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                            isChecked
                              ? "bg-jove border-jove"
                              : "border-space-30 bg-surface"
                          }`}>
                            {isChecked && (
                              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`font-medium text-sm transition-colors ${isChecked ? "text-space" : "text-space-60"}`}>
                              {item.name}
                            </div>
                            <div className="text-xs text-space-40 line-clamp-2 mt-0.5 leading-relaxed">
                              {item.description}
                            </div>
                            {item.errors.length > 0 && (
                              <div className="text-[11px] text-red-600 mt-1 flex items-center gap-1">
                                <span>⚠</span> {item.errors.join(", ")}
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "create" && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-space-60 mb-1.5">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="my-skill"
                  className="w-full rounded-lg border border-space-30 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-jove-60/20 focus:border-jove-60 transition-colors"
                />
                <p className="text-[11px] text-space-40 mt-1.5">
                  Lowercase, digits, single hyphens. Max 64 chars.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-space-60 mb-1.5">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="When to use this skill and what it does."
                  rows={3}
                  className="w-full rounded-lg border border-space-30 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-jove-60/20 focus:border-jove-60 transition-colors"
                />
              </div>
              {createError && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                  {createError}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="px-5 py-3 border-t border-space-20 flex flex-col-reverse sm:flex-row justify-end gap-2 shrink-0">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          {tab === "install" && (
            <Button
              size="sm"
              onClick={runInstall}
              disabled={!preview || selected.size === 0 || installing}
              loading={installing}
            >
              {`Install ${selected.size || 0} skill${selected.size === 1 ? "" : "s"}`}
            </Button>
          )}
          {tab === "create" && (
            <Button
              size="sm"
              onClick={runCreate}
              disabled={!name.trim() || !description.trim() || creating}
              loading={creating}
            >
              Create & Edit
            </Button>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  );
}

// ─── toggle switch ────────────────────────────────────────────────────────

function ToggleSwitch({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="flex items-center gap-2 shrink-0 select-none group"
    >
      <span
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ${
          checked ? "bg-jove" : "bg-space-30"
        } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer group-hover:shadow-sm"}`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-surface shadow-sm transition-transform duration-200 ${
            checked ? "translate-x-4" : "translate-x-0.5"
          }`}
        />
      </span>
      {label && (
        <span className="text-[11px] text-space-50 hidden sm:inline">{label}</span>
      )}
    </button>
  );
}

// ─── MCP server picker ────────────────────────────────────────────────────

function McpServerPicker({
  selected,
  available,
  readonly,
  onAdd,
  onRemove,
  allServers,
}: {
  selected: string[];
  available: string[];
  readonly?: boolean;
  onAdd: (name: string) => void;
  onRemove: (name: string) => void;
  allServers: { name: string; status: string; enabled: boolean }[];
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const getStatus = (name: string) => {
    const srv = allServers.find((s) => s.name === name);
    if (!srv) return "unknown";
    if (!srv.enabled) return "disabled";
    return srv.status;
  };

  const statusDot = (status: string) => {
    const color =
      status === "connected" || status === "running"
        ? "bg-emerald-500"
        : status === "failed"
          ? "bg-red-400"
          : "bg-space-40";
    return <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${color}`} />;
  };

  return (
    <div className="min-h-[34px]" ref={containerRef}>
      {/* Selected chips */}
      <div className="flex flex-wrap gap-1.5 items-center">
        {selected.map((m) => {
          const status = getStatus(m);
          return (
            <span
              key={m}
              className="inline-flex items-center gap-1.5 bg-jove-10 text-jove text-[11px] px-2.5 py-1 rounded-full border border-jove-30"
            >
              {statusDot(status)}
              <code className="font-medium">{m}</code>
              {!readonly && (
                <button
                  onClick={() => onRemove(m)}
                  className="hover:text-red-600 transition-colors ml-0.5 leading-none"
                >
                  ×
                </button>
              )}
            </span>
          );
        })}

        {/* Add button + dropdown */}
        {!readonly && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setOpen(!open)}
              className="inline-flex items-center gap-1 text-[11px] text-space-50 hover:text-jove border border-dashed border-space-30 hover:border-jove-60 rounded-full px-2.5 py-1 transition-colors"
            >
              <span>+</span>
              <span>Add server</span>
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {open && (
              <div className="absolute z-10 mt-1 left-0 w-56 bg-surface rounded-lg border border-space-20 shadow-lg py-1 max-h-48 overflow-y-auto">
                {available.length === 0 ? (
                  <p className="px-3 py-2 text-[11px] text-space-40 italic">
                    {allServers.length === 0
                      ? "No MCP servers configured"
                      : "All servers already added"}
                  </p>
                ) : (
                  available.map((name) => {
                    const status = getStatus(name);
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => {
                          onAdd(name);
                          if (available.length <= 1) setOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-jove-10 transition-colors flex items-center gap-2"
                      >
                        {statusDot(status)}
                        <span className="font-mono font-medium text-space">{name}</span>
                        <span className="ml-auto text-[10px] text-space-40 capitalize">{status}</span>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Empty hint */}
      {selected.length === 0 && readonly && (
        <p className="text-[11px] text-space-40 italic mt-1">None required</p>
      )}
    </div>
  );
}

// ─── editor modal ─────────────────────────────────────────────────────────

function SkillEditorModal({
  skillId,
  onClose,
}: {
  skillId: number;
  onClose: () => void;
}) {
  const { getSkillDetail, saveSkill, validate, openFolder } = useSkillsStore();
  const { servers: mcpServers, loadServers: loadMcpServers } = useMcpStore();
  const [skill, setSkill] = useState<Skill | null>(null);
  const [body, setBody] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [requiresMcp, setRequiresMcp] = useState<string[]>([]);
  const [extraFiles, setExtraFiles] = useState<{ name: string; size: number }[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getSkillDetail(skillId).then((res) => {
      setSkill(res.skill);
      setName(res.skill.name);
      setDescription(res.skill.description || "");
      setBody(res.body);
      setRequiresMcp(res.skill.requires_mcp);
      setExtraFiles(res.extra_files || []);
      setLoaded(true);
    });
    loadMcpServers();
  }, [skillId, getSkillDetail, loadMcpServers]);

  const availableMcpNames = useMemo(
    () =>
      mcpServers
        .map((s) => s.name)
        .filter((n) => !requiresMcp.includes(n)),
    [mcpServers, requiresMcp],
  );

  const readonly = skill?.scope === "bundled";

  const runValidate = async () => {
    const res = await validate({
      name,
      description,
      body,
      requires_mcp: requiresMcp,
    });
    setErrors(res.errors);
  };

  const runSave = async () => {
    if (readonly || !skill) return;
    setSaving(true);
    try {
      await saveSkill(skill.id, {
        name,
        description,
        body,
        requires_mcp: requiresMcp,
      });
      onClose();
    } catch (err) {
      alert("Save failed: " + (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-surface rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <header className="px-5 py-4 border-b border-space-20 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-base font-semibold text-space truncate">
              {loaded ? name || "Untitled" : "Loading…"}
            </h2>
            {skill && <ScopeBadge scope={skill.scope} />}
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-space-40 hover:text-space-60 hover:bg-space-10 transition-colors shrink-0"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        {/* Body */}
        <div className="p-5 overflow-y-auto flex-1 min-h-0 space-y-4">
          {!loaded || !skill ? (
            <div className="flex items-center justify-center py-12 text-xs text-space-40 italic">
              Loading skill…
            </div>
          ) : (
            <>
              {readonly && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-800 leading-relaxed">
                  This skill ships with OmniWerk and is read-only. To modify,
                  edit{" "}
                  <code className="font-mono bg-amber-100 px-1 rounded">
                    {skill.path}/SKILL.md
                  </code>{" "}
                  directly.
                </div>
              )}

              {/* Skill directory info bar */}
              <div className="rounded-lg bg-space-10 px-3.5 py-2.5 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[11px] text-space-40 font-mono truncate flex-1">
                    {skill.path}
                  </div>
                  <button
                    type="button"
                    onClick={() => openFolder(skillId)}
                    className="inline-flex items-center gap-1.5 text-[11px] font-medium text-jove hover:text-jove-200 bg-surface border border-space-20 rounded-lg px-2.5 py-1 transition-colors shrink-0 shadow-sm hover:shadow"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                    </svg>
                    Open folder
                  </button>
                </div>
                {extraFiles.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-space-50">
                    <span>Also includes:</span>
                    {extraFiles.slice(0, 5).map((f) => (
                      <code
                        key={f.name}
                        className="bg-surface border border-space-20 text-space-60 rounded px-1.5 py-px"
                      >
                        {f.name}
                      </code>
                    ))}
                    {extraFiles.length > 5 && (
                      <span className="text-space-40">
                        +{extraFiles.length - 5} more
                      </span>
                    )}
                  </div>
                )}
              </div>

              {errors.length > 0 && (
                <ul className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700 list-disc list-inside space-y-0.5">
                  {errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-space-60 mb-1.5">
                    Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    disabled={readonly}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full rounded-lg border border-space-30 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-jove-60/20 focus:border-jove-60 transition-colors disabled:bg-space-10 disabled:text-space-40"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-space-60 mb-1.5">
                    Required MCP servers
                  </label>
                  <McpServerPicker
                    selected={requiresMcp}
                    available={availableMcpNames}
                    readonly={readonly}
                    onAdd={(n) => setRequiresMcp([...requiresMcp, n])}
                    onRemove={(n) => setRequiresMcp(requiresMcp.filter((x) => x !== n))}
                    allServers={mcpServers}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-space-60 mb-1.5">
                  Description
                </label>
                <textarea
                  value={description}
                  disabled={readonly}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border border-space-30 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-jove-60/20 focus:border-jove-60 transition-colors disabled:bg-space-10 disabled:text-space-40"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-space-60 mb-1.5">
                  Body (Markdown)
                </label>
                <textarea
                  value={body}
                  disabled={readonly}
                  onChange={(e) => setBody(e.target.value)}
                  rows={14}
                  spellCheck={false}
                  className="w-full rounded-lg border border-space-30 px-3 py-2 text-xs font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-jove-60/20 focus:border-jove-60 transition-colors disabled:bg-space-10 disabled:text-space-40 resize-y"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <footer className="px-5 py-3 border-t border-space-20 flex flex-col-reverse sm:flex-row justify-end gap-2 shrink-0">
          <Button variant="ghost" size="sm" onClick={runValidate}>
            Validate
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {readonly ? "Close" : "Cancel"}
          </Button>
          {!readonly && (
            <Button
              size="sm"
              onClick={runSave}
              disabled={saving}
              loading={saving}
            >
              Save
            </Button>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  );
}
